/**
 * Het tabblad Groepen: links de groepen van deze leerkracht, rechts de
 * leerlingen van de gekozen groep, met hun gebruikersnaam en wachtwoord (zo
 * kan de leerkracht ze opzoeken als een leerling het vergeet).
 *
 * Een nieuwe leerling krijgt het standaardniveau van de groep, dat uit het
 * leerjaar volgt; de leerkracht kan een ander niveau kiezen. Een groep met
 * lessen kan niet worden verwijderd; de server zegt dan waarom niet.
 *
 * Voorlezen staat per leerling uit, gewoon of rustig (standaard uit): bij het
 * toevoegen, of in de lijst. Alleen een leerling bij wie het aan staat, ziet
 * bij het lezen de luidspreker bij elke alinea. Rustig: een pauze tussen de
 * zinnen, en de rustige stem als de beheerder die koos (LLM Config).
 *
 * Bij de moedertaal staat of de vertaling ook kan worden voorgelezen (stap
 * 16): in de keuzelijst, eronder in een zin, en in de lijst met een
 * luidspreker. Dat kan in een taal die het voorleesmodel kent en die de
 * beheerder niet uitzette (/teacher/vertaling-voorlezen); de leerling hoort
 * het alleen als Voorlezen voor hem aanstaat.
 *
 * Per leerling kan de leerkracht een papieren toets maken (stap 24, van kruk
 * naar trap; onderdelen/PapierenToets.js): de leerling maakt hem, ze kijken
 * hem samen na, en wat ze samen besluiten (een stap hoger, of nog niet),
 * legt de leerkracht daar vast. Een stap hoger verandert dan het niveau
 * hier, met dezelfde melding als bij de keuzelijst.
 */
import React, { useEffect, useState } from 'react';
import { FileText, Plus, Save, Trash2, Volume2, VolumeX, X } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { LEERJAREN, MOEDERTALEN, NIVEAUS } from '../../../keuzelijsten';
import { PapierenToets } from '../onderdelen/PapierenToets';

const NIEUWE_GROEP = { naam: '', leerjaar: '4' };
const nieuweLeerling = (niveau) => ({ username: '', password: '', naam: '', niveau, moedertaal: '',
  voorlezen: false, voorleestempo: 'gewoon' });

/** Uit, gewoon of rustig: wat er in de keuzelijst staat. */
const voorleeskeuze = (leerling) => (leerling.voorlezen ? (leerling.voorleestempo || 'gewoon') : 'uit');

function VoorleesOpties() {
  return (
    <>
      <option value="uit">Uit</option>
      <option value="gewoon">Gewoon</option>
      <option value="rustig">Rustig</option>
    </>
  );
}

/**
 * Of de vertaling in deze moedertaal kan worden voorgelezen: 'aan', 'uit'
 * (kan, maar de beheerder zette het uit), 'nee' (kan niet in deze taal), of
 * null (Nederlands, of nog niet bekend).
 */
function vertalingVoorlezen(taal, talen) {
  if (!taal || !talen) return null;
  if (talen.aan.includes(taal)) return 'aan';
  return talen.mogelijk.includes(taal) ? 'uit' : 'nee';
}

/** De naam van de taal in de keuzelijst, met of de vertaling ook kan worden voorgelezen of alleen te lezen is. */
function taalLabel(taal, talen) {
  const kan = vertalingVoorlezen(taal, talen);
  if (!taal) return 'Nederlands';
  // kort: het veld is smal; de zin eronder zegt het voluit
  if (kan === 'aan') return `${taal} (ook voorlezen)`;
  if (kan) return `${taal} (alleen lezen)`;
  return taal;
}

/** Onder de keuze van de moedertaal: wat de leerling met de vertaling kan. */
function VertalingUitleg({ taal, voorlezen, talen }) {
  const kan = vertalingVoorlezen(taal, talen);
  if (!kan) return null;
  let tekst;
  if (kan === 'aan') {
    tekst = voorlezen
      ? `De leerling kan de vertaling in het ${taal} lezen en laten voorlezen.`
      : `De leerling kan de vertaling in het ${taal} lezen. Laten voorlezen kan ook, als je Voorlezen aanzet.`;
  } else if (kan === 'uit') {
    tekst = `De leerling kan de vertaling in het ${taal} lezen. Voorlezen in het ${taal} staat (nog) uit; `
      + 'de beheerder kan het aanzetten in LLM Config.';
  } else {
    tekst = `De leerling kan de vertaling in het ${taal} lezen. Voorlezen kan niet in het ${taal}: `
      + 'die taal kent het voorleesmodel niet.';
  }
  return (
    <p className="text-muted text-small" style={{ gridColumn: '1 / -1', margin: 0, display: 'flex', gap: 6, alignItems: 'center' }}
      data-testid="vertaling-uitleg">
      {kan === 'aan' ? <Volume2 size={14} /> : <VolumeX size={14} />} {tekst}
    </p>
  );
}

export function Groepen({ actief, meldFout }) {
  const [groepen, setGroepen] = useState([]);
  const [laden, setLaden] = useState(false);
  const [gekozen, setGekozen] = useState(null);
  const [leerlingen, setLeerlingen] = useState([]);
  const [standaardNiveau, setStandaardNiveau] = useState('2F');

  const [groepOpen, setGroepOpen] = useState(false);
  const [nieuweGroep, setNieuweGroep] = useState(NIEUWE_GROEP);
  const [leerlingOpen, setLeerlingOpen] = useState(false);
  const [nieuw, setNieuw] = useState(nieuweLeerling(''));
  const [vertalingTalen, setVertalingTalen] = useState(null);   // { aan, mogelijk }, of null zolang onbekend
  const [niveauMelding, setNiveauMelding] = useState(null);     // wat een nieuw niveau deed in de lessen
  const [papierVoor, setPapierVoor] = useState(null);           // de leerling voor wie een papieren toets open is

  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    api.haalGroepen().then(setGroepen).catch(err => meldFout(err.message)).finally(() => setLaden(false));
    // Lukt dit niet, dan staat er bij de moedertaal niets over voorlezen; de rest werkt gewoon.
    api.haalVertalingVoorlezen().then(setVertalingTalen).catch(() => setVertalingTalen(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  const laadLeerlingen = async (groepId) => {
    try {
      setLeerlingen(await api.haalLeerlingen(groepId));
    } catch (err) {
      meldFout(err.message);
    }
  };

  const kies = async (groep) => {
    setGekozen(groep);
    laadLeerlingen(groep.id);
    try {
      const { niveau } = await api.haalStandaardniveau(groep.id);
      setStandaardNiveau(niveau);
      setNieuw(prev => ({ ...prev, niveau }));
    } catch (err) {
      console.error('Fout bij laden standaard niveau:', err);
    }
  };

  const bewaarGroep = async (e) => {
    e.preventDefault();
    try {
      await api.maakGroep(nieuweGroep.naam, nieuweGroep.leerjaar ? parseInt(nieuweGroep.leerjaar) : null);
      setGroepen(await api.haalGroepen());
      setGroepOpen(false);
      setNieuweGroep(NIEUWE_GROEP);
    } catch (err) {
      meldFout(err.message);
    }
  };

  const verwijderGroep = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze groep wilt verwijderen?')) return;
    try {
      await api.verwijderGroep(id);
      setGroepen(groepen.filter(g => g.id !== id));
      if (gekozen?.id === id) {
        setGekozen(null);
        setLeerlingen([]);
      }
    } catch (err) {
      meldFout(err.message);
    }
  };

  const bewaarLeerling = async (e) => {
    e.preventDefault();
    if (!gekozen) return;
    try {
      // Leeg niveau: het standaardniveau van de groep; lege moedertaal: Nederlands.
      await api.maakLeerling(gekozen.id, { ...nieuw, niveau: nieuw.niveau || null, moedertaal: nieuw.moedertaal || null });
      laadLeerlingen(gekozen.id);
      setLeerlingOpen(false);
      setNieuw(nieuweLeerling(standaardNiveau));
    } catch (err) {
      meldFout(err.message);
    }
  };

  const verwijderLeerling = async (id) => {
    const zeker = window.confirm(
      'Weet je zeker dat je deze leerling wilt verwijderen?\n\n' +
      '⚠️ LET OP: Alle gegevens van deze leerling worden permanent verwijderd, inclusief:\n' +
      '• Toetsresultaten\n' +
      '• Ontwikkelingsdata en voortgang\n' +
      '• Sessie-instellingen\n\n' +
      'Deze actie kan niet ongedaan worden gemaakt.'
    );
    if (!zeker) return;
    try {
      await api.verwijderLeerling(id);
      setLeerlingen(leerlingen.filter(l => l.id !== id));
    } catch (err) {
      meldFout(err.message);
    }
  };

  const zetLeerling = (veld) => (e) => setNieuw({ ...nieuw, [veld]: e.target.value });

  const zetVoorlezen = async (leerling, keuze) => {
    try {
      const bijgewerkt = await api.zetVoorlezen(leerling.id, keuze !== 'uit', keuze === 'uit' ? undefined : keuze);
      setLeerlingen(huidig => huidig.map(l => (l.id === leerling.id
        ? { ...l, voorlezen: bijgewerkt.voorlezen, voorleestempo: bijgewerkt.voorleestempo } : l)));
    } catch (err) {
      meldFout(err.message);
    }
  };

  /** Een ander niveau: meteen opslaan, en zeggen wat het doet in de lessen van de leerling. */
  const zetNiveau = async (leerling, niveau) => {
    if (niveau === leerling.niveau) return;
    try {
      const uit = await api.zetNiveau(leerling.id, niveau);
      setLeerlingen(huidig => huidig.map(l => (l.id === leerling.id ? { ...l, niveau: uit.leerling.niveau } : l)));
      setNiveauMelding({ naam: leerling.naam, van: leerling.niveau, naar: uit.leerling.niveau, ...uit });
    } catch (err) {
      meldFout(err.message);
    }
  };

  if (laden) return <p>Laden...</p>;

  const klein = { fontSize: 12, padding: '6px 10px' };
  const klein13 = { fontSize: 13, padding: '6px 8px' };
  const code = { background: 'var(--vlak-2)', padding: '2px 4px', borderRadius: 3, fontSize: 12 };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20 }}>
      <div className="card" style={{ fontSize: 13 }}>
        <div className="flex-between mb-16">
          <h2 style={{ fontSize: 18 }}>Groepen</h2>
          <button className="btn btn-primary" style={klein} onClick={() => setGroepOpen(!groepOpen)}>
            <Plus size={14} /> Nieuwe groep
          </button>
        </div>

        {groepOpen && (
          <form onSubmit={bewaarGroep} className="card mb-16" style={{ background: 'var(--vlak)', padding: 12 }}>
            <div style={{ display: 'grid', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 12 }}>Groepsnaam</label>
                <input type="text" className="input" style={klein13} placeholder="bijv. Groep 5a" value={nieuweGroep.naam}
                  onChange={e => setNieuweGroep({ ...nieuweGroep, naam: e.target.value })} required />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 12 }}>Leerjaar</label>
                <select className="input" style={klein13} value={nieuweGroep.leerjaar}
                  onChange={e => setNieuweGroep({ ...nieuweGroep, leerjaar: e.target.value })}>
                  {LEERJAREN.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-8 mt-8">
              <button type="submit" className="btn btn-success" style={klein}><Save size={14} /> Opslaan</button>
              <button type="button" className="btn btn-secondary" style={klein} onClick={() => setGroepOpen(false)}>Annuleren</button>
            </div>
          </form>
        )}

        {groepen.map(groep => (
          <div
            key={groep.id}
            className={`card ${gekozen?.id === groep.id ? 'selected' : ''}`}
            style={{ cursor: 'pointer', background: gekozen?.id === groep.id ? 'var(--blauw-vlak)' : 'var(--wit)', marginBottom: 8, padding: '10px 12px' }}
            onClick={() => kies(groep)}
          >
            <div className="flex-between">
              <div>
                <strong>{groep.naam}</strong>
                {groep.leerjaar && (
                  <span className="badge badge-primary" style={{ marginLeft: 6, fontSize: 11 }}>Groep {groep.leerjaar}</span>
                )}
                <span className="text-muted" style={{ marginLeft: 6, fontSize: 12 }}>{groep.student_count} leerlingen</span>
              </div>
              <button className="btn btn-danger" style={{ padding: '4px 8px' }} onClick={(e) => { e.stopPropagation(); verwijderGroep(groep.id); }}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {!gekozen ? (
          <p className="text-muted text-center">Selecteer een groep om leerlingen te bekijken</p>
        ) : (
          <>
            <div className="flex-between mb-16">
              <h2>Leerlingen - {gekozen.naam}</h2>
              <button className="btn btn-primary" onClick={() => {
                if (!leerlingOpen) setNieuw(prev => ({ ...prev, niveau: standaardNiveau }));
                setLeerlingOpen(!leerlingOpen);
              }}>
                <Plus size={16} /> Leerling toevoegen
              </button>
            </div>

            {leerlingOpen && (
              <form onSubmit={bewaarLeerling} className="card mb-16" style={{ background: 'var(--vlak)' }} autoComplete="off">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  <div className="form-group">
                    <label>Naam</label>
                    <input type="text" className="input" value={nieuw.naam} onChange={zetLeerling('naam')} required />
                  </div>
                  <div className="form-group">
                    <label>Gebruikersnaam</label>
                    <input type="text" className="input" value={nieuw.username} onChange={zetLeerling('username')} autoComplete="new-username" required />
                  </div>
                  <div className="form-group">
                    <label>Wachtwoord</label>
                    <input type="text" className="input" value={nieuw.password} onChange={zetLeerling('password')} autoComplete="off" required />
                  </div>
                  <div className="form-group">
                    <label>Niveau</label>
                    <select className="input" value={nieuw.niveau} onChange={zetLeerling('niveau')}>
                      {NIVEAUS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="nieuw-moedertaal">Moedertaal</label>
                    <select id="nieuw-moedertaal" className="input" value={nieuw.moedertaal} onChange={zetLeerling('moedertaal')}>
                      {MOEDERTALEN.map(m => <option key={m} value={m}>{taalLabel(m, vertalingTalen)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="nieuw-voorlezen">Voorlezen</label>
                    <select id="nieuw-voorlezen" className="input" value={voorleeskeuze(nieuw)}
                      onChange={e => setNieuw({ ...nieuw, voorlezen: e.target.value !== 'uit',
                        voorleestempo: e.target.value === 'uit' ? nieuw.voorleestempo : e.target.value })}>
                      <VoorleesOpties />
                    </select>
                  </div>
                  <VertalingUitleg taal={nieuw.moedertaal} voorlezen={nieuw.voorlezen} talen={vertalingTalen} />
                </div>
                <div className="flex gap-8 mt-16">
                  <button type="submit" className="btn btn-success"><Save size={16} /> Opslaan</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setLeerlingOpen(false)}>Annuleren</button>
                </div>
              </form>
            )}

            {niveauMelding && <NiveauMelding melding={niveauMelding} onSluit={() => setNiveauMelding(null)} />}
            {papierVoor && (
              <PapierenToets leerling={papierVoor} onSluit={() => setPapierVoor(null)} meldFout={meldFout}
                onNieuwNiveau={(uit) => {
                  setLeerlingen(huidig => huidig.map(l => (l.id === uit.leerling.id ? { ...l, niveau: uit.leerling.niveau } : l)));
                  setNiveauMelding({ naam: papierVoor.naam, van: papierVoor.niveau, naar: uit.leerling.niveau, ...uit });
                }} />
            )}

            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Naam</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Gebruiker</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Wachtwoord</th>
                    <th>Niveau</th>
                    <th>Taal</th>
                    <th style={{ whiteSpace: 'nowrap' }}><Volume2 size={12} style={{ verticalAlign: 'middle' }} /> Voorlezen</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {leerlingen.map(l => (
                    <tr key={l.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{l.naam}</td>
                      <td><code style={code}>{l.username}</code></td>
                      <td><code style={code}>{l.plain_password || '••••'}</code></td>
                      <td>
                        <select className="input" style={{ fontSize: 12, padding: '2px 4px', width: 'auto' }}
                          value={l.niveau || ''} onChange={e => zetNiveau(l, e.target.value)} aria-label={`Niveau van ${l.naam}`}>
                          {!NIVEAUS.includes(l.niveau) && <option value={l.niveau || ''}>{l.niveau || '-'}</option>}
                          {NIVEAUS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {l.moedertaal || 'NL'}
                        <TaalIcoon taal={l.moedertaal} talen={vertalingTalen} />
                      </td>
                      <td>
                        <select className="input" style={{ fontSize: 12, padding: '2px 4px', width: 'auto' }}
                          value={voorleeskeuze(l)} onChange={e => zetVoorlezen(l, e.target.value)}
                          aria-label={`Voorlezen voor ${l.naam}`}>
                          <VoorleesOpties />
                        </select>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-secondary" style={{ padding: '4px 8px', marginRight: 4 }} onClick={() => setPapierVoor(l)}
                          title={`Papieren toets voor ${l.naam}`} aria-label={`Papieren toets voor ${l.naam}`}>
                          <FileText size={12} />
                        </button>
                        <button className="btn btn-danger" style={{ padding: '4px 8px' }} onClick={() => verwijderLeerling(l.id)}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {vertalingTalen && leerlingen.some(l => l.moedertaal) && (
              <p className="text-muted text-small mt-8" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Volume2 size={12} /> bij de taal: de vertaling kan ook worden voorgelezen, als Voorlezen aanstaat.
                <VolumeX size={12} style={{ marginLeft: 8 }} /> alleen lezen.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** In de lijst, achter de moedertaal: of de vertaling kan worden voorgelezen. */
function TaalIcoon({ taal, talen }) {
  const kan = vertalingVoorlezen(taal, talen);
  if (!kan) return null;
  const uitleg = kan === 'aan' ? `De vertaling in het ${taal} kan ook worden voorgelezen`
    : `De vertaling in het ${taal} is alleen te lezen`;
  const Icoon = kan === 'aan' ? Volume2 : VolumeX;
  return (
    <span title={uitleg} aria-label={uitleg} role="img" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
      <Icoon size={12} style={{ color: kan === 'aan' ? 'var(--accent)' : 'var(--tekst-3)' }} />
    </span>
  );
}

/**
 * Wat een nieuw niveau doet in de lessen van de leerling (de server zegt
 * het): op het nieuwe niveau in de lessen die hij nog niet begon; vast in de
 * lessen die hij al begon; en een eigen niveau voor de les gaat voor.
 */
function NiveauMelding({ melding, onSluit }) {
  const { naam, van, naar, nieuw, vast, eigen, voorbereid = [] } = melding;
  const lijst = (lessen) => lessen.join(', ');
  return (
    <div className="alert alert-success mb-16" data-testid="niveau-melding" style={{ fontSize: 13, position: 'relative', paddingRight: 36 }}>
      <strong>{naam} leest nu op {naar}</strong>{van && van !== naar ? ` (was ${van})` : ''}.
      {nieuw.length > 0 && (
        <div>
          Op {naar} in: {lijst(nieuw)}.
          {voorbereid.length > 0 && voorbereid.length === nieuw.length && ' De tekst en de toets op dat niveau worden alvast gemaakt.'}
          {voorbereid.length > 0 && voorbereid.length < nieuw.length
            && ` Voor de actieve lessen (${lijst(voorbereid)}) worden de tekst en de toets alvast gemaakt; de andere als ${naam} ze opent.`}
        </div>
      )}
      {vast.length > 0 && <div>Blijft op het oude niveau, want al begonnen: {lijst(vast)}.</div>}
      {eigen.length > 0 && <div>Eigen niveau voor de les (tandwiel bij de les), dat blijft: {lijst(eigen)}.</div>}
      {nieuw.length + vast.length + eigen.length === 0 && <div>Het geldt vanaf de volgende les.</div>}
      <button type="button" onClick={onSluit} aria-label="Melding sluiten"
        style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
        <X size={14} />
      </button>
    </div>
  );
}

